import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { DayjsFormats, logger } from 'shared/lib'
import { CalendarFilterStore } from 'shared/ui/CalendarDropdown/store/CalendarFilterStore'
import { CalendarFilterEnum } from 'shared/ui/CalendarDropdown/calendarEnum'
import { EnumDropdownItemVariant, type IDropdownItem, showToast } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { OrganizationApi } from 'entities/Organization'
import { type IResponseOrganizationEventTypes } from 'entities/Organization/api/types'
import { RequestAuditLogsModalContent } from 'pages/settings/pages/doubleOptIn/ui/RequestAuditLogs/RequestAuditLogsModalContent'

export class RequestAuditLogsStore {
  modalId = nanoid()
  private _loading = false
  private _calendarFilterStore: CalendarFilterStore
  private _selectedMembers: IDropdownItem[] = []
  private _events: IResponseOrganizationEventTypes = []
  private _selectedEventTypes: string[] = []

  constructor(private _entities: IEntitiesStore) {
    this._calendarFilterStore = new CalendarFilterStore(
      this._entities.organizationStore.hipaaEnabledAt
    )

    makeAutoObservable(this)
    this.calendarFilterStore.setSelectedCalendarFilter(CalendarFilterEnum.last30days)
    this.calendarFilterStore.setParamsDate()
  }

  init = async () => {
    try {
      const { data } = await OrganizationApi.getOrganizationEventTypes()

      runInAction(() => {
        const events = [
          {
            key: 'all',
            name: 'All events',
            description: 'All events',
          },
          ...data,
        ]

        if (data.length) this._events = events
      })
    } catch (e) {
      logger.error(e)
    }
  }

  onOpenModal = () => {
    modalStore.addModal({
      id: this.modalId,
      type: ModalTypeList.DEFAULT,
      title: 'Request audit logs',
      width: 416,
      ModalContent: () => <RequestAuditLogsModalContent store={this} />,
      primaryAction: {
        text: 'Request',
        onAction: async () => {
          try {
            if (this._loading) return

            this._loading = true
            await OrganizationApi.getOrganizationExport(this.params)
            modalStore.closeModal(this.modalId)

            showToast({
              type: 'info',
              title:
                'Your request for audit logs has been received. We’ll email you a secure download link as soon as your file is ready.',
            })
          } catch (e) {
            logger.error(e)

            if (e instanceof AxiosError) {
              showToast({
                type: 'error',
                title:
                  e.errors.getError('message') ||
                  e.errors.getError('default') ||
                  'Something went wrong. Please try again',
              })
            }
          } finally {
            this._loading = false
          }
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => modalStore.closeModal(this.modalId),
      },
    })
  }

  setSelectedMembers = (value: IDropdownItem) => {
    this._selectedMembers = [...this._selectedMembers, value]
  }

  deleteSelectedMembers = (value: IDropdownItem) => {
    this._selectedMembers = this._selectedMembers.filter((item) => item.id !== value.id)
  }

  selectAllEvents = () => {
    this._selectedEventTypes = this._events.map((el) => el.key)
  }

  deselectAllEvents = () => {
    this._selectedEventTypes = []
  }

  handleSelectAllToggle = () => {
    if (this.isSelectAllEvents) {
      this.deselectAllEvents()
    } else {
      this.selectAllEvents()
    }
  }

  handleEventsTypeChange = (item: IDropdownItem) => {
    if (item.id === 'all') {
      this.handleSelectAllToggle()
    } else {
      this.toggleEventTypeSelection(item.id.toString())
    }
  }

  toggleEventTypeSelection = (id: string) => {
    if (this._selectedEventTypes.includes(id)) {
      this._selectedEventTypes = this._selectedEventTypes.filter(
        (typeId) => typeId !== id && typeId !== 'all'
      )
    } else {
      this._selectedEventTypes = [...this._selectedEventTypes, id]
      if (
        this._selectedEventTypes.length ===
        this.eventTypeOptions.filter((item) => item.id !== 'all').length
      ) {
        this._selectedEventTypes = ['all', ...this._selectedEventTypes]
      }
    }
  }

  get calendarFilterStore() {
    return this._calendarFilterStore
  }

  get members(): IDropdownItem[] {
    return this._entities.usersStore.membersList.map((item) => ({
      id: item.id,
      label: item.name,
      value: item.name,
      data: item,
      avatarProps: {
        online: !item.isAway,
        size: 16,
        info: item.avatarInfo,
      },
    }))
  }

  get selectedMembers() {
    return this._selectedMembers
  }

  get isSelectAllEvents() {
    return this._selectedEventTypes.includes('all')
  }

  get selectedEventTypes(): IDropdownItem[] {
    return this.eventTypeOptions.filter((item: IDropdownItem) => item.activeValue)
  }

  get eventTypeOptions(): IDropdownItem[] {
    return this._events
      .map((item) => ({
        id: item.key,
        label: item.name,
        variant: EnumDropdownItemVariant.Checkbox,
        activeValue: this._selectedEventTypes.includes(item.key),
        labelWidth: '100%',
        divider: item.key === 'all',
      }))
      .sort((a) => (a.id === 'all' ? -1 : 0))
  }

  get params() {
    return {
      events: this._selectedEventTypes.filter((item) => item !== 'all'),
      user_ids: this._selectedMembers.map((item) => item.id),
      date_from: this._calendarFilterStore.sendDate?.from
        ? uiStore
            .dayjsWithoutTimezone(this._calendarFilterStore.sendDate.from)
            .format(DayjsFormats.timestampServerDate)
        : '',
      date_to: this._calendarFilterStore.sendDate?.to
        ? uiStore
            .dayjsWithoutTimezone(this._calendarFilterStore.sendDate.to)
            .format(DayjsFormats.timestampServerDate)
        : '',
      timezone: uiStore.timezone,
    }
  }
}
